import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import DataChart from '../components/DataChart';
import DataTable from '../components/DataTable';
import FormattedDataTable from '../components/FormattedDataTable';
import DateRangePicker from '../components/DateRangePicker';
import DataTypeSelector from '../components/DataTypeSelector';
import FacilityFilter from '../components/FacilityFilter';
import RawDataBanner from '../components/RawDataBanner';
import { useCache } from '../hooks/useCache';
import { useCustomerFilter } from '../hooks/useCustomerFilter';
import { useScopeSync } from '../hooks/useScopeSync';
import { useCurrentScope } from '../hooks/useCurrentScope';
import { useScope } from '../contexts/ScopeContext';
import ScopeBar from '../components/ScopeBar';
import GroupFacilitiesModal from '../components/GroupFacilitiesModal';
import DirectionToggle from '../components/DirectionToggle';
import {
  rowMatchesFacilityScope,
  loadDirectionPreference,
  saveDirectionPreference,
  directionShortLabel,
  dataTypeHasDirection,
  DIRECTION_OPTIONS,
} from '../utils/referralDirection';
import { exportRecordsToCsv, exportRecordsToXlsx, buildFilenameStem, triggerDownload } from '../utils/exportData';
import {
  exportFormattedReferralsToCsv,
  exportFormattedReferralsToXlsx,
  isFormattedReferralExportType,
} from '../utils/exportFormattedReferrals';
import client from '../api/client';

function extractCacheRecordIds(records) {
  return (records || [])
    .map((record) => {
      // Cache ExternalId lives on record.id — never fall back to referral_number
      // or referral_id; those collide across unrelated referrals and caused
      // wrong-facility rows when matching live PHI.
      const id = record?.id;
      return id != null && id !== '' ? String(id) : null;
    })
    .filter(Boolean);
}

const DATA_TYPES = [
  { id: 'referrals', name: 'Referrals', description: 'Collaboration and referral data' },
  { id: 'vitalrounds', name: 'VitalRounds', description: 'VitalRounds collaboration data' },
  { id: 'messages', name: 'Messages', description: 'VChat message usage analytics' },
  // 'groups' (rpt_consolidated_group_usage daily VChat group activity) was
  // retired 2026-05 — never a metric we needed; the original intent was
  // referral-routing-by-group, which is now exposed as 'group-referrals'.
  { id: 'logins', name: 'Logins', description: 'User login platform usage' },
  { id: 'studies', name: 'Studies', description: 'DICOM studies (VitalPACS) — daily aggregates' },
  // V2.1 endpoints (added 2026-05).
  // - 'group-referrals': referrals routed to a group / queue (transfer
  //   centers, intake groups, referral wizard).
  // - 'faxes': one row per inbound/outbound fax.
  // - 'study-details': one row per study (description + modality), distinct
  //   from 'studies' which is the daily aggregate.
  // 'fax-recipients' is intentionally NOT exposed as a tile — it's a
  //   per-user join table only useful for VERA queries (e.g. "what % of
  //   faxes go unread"). Add it here later if a use case emerges.
  { id: 'group-referrals', name: 'Group Referrals', description: 'Referrals routed to a group/queue' },
  { id: 'faxes', name: 'Faxes', description: 'Per-fax records (sent/received/failed)' },
  { id: 'study-details', name: 'Study Details', description: 'Per-study DICOM rows (description + modality)' }
];

// 'YYYY-MM-DD' -> 'M/D/YYYY' for the inline date-range hint and the
// compact filter strip. Module-scoped so the dateRangeLabel useMemo
// inside the component can call it without hitting a temporal-dead-
// zone error from the const-arrow form.
const formatDateShort = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const [y, m, d] = String(yyyymmdd).split('-');
  if (!y || !m || !d) return yyyymmdd;
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
};

function Analytics() {
  const cache = useCache();

  const [selectedType, setSelectedType] = useState('referrals');
  const [dateRangePreset, setDateRangePreset] = useState('last_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Per-data-type result caches keyed by data type only. The cache is
  // wiped whenever the date range changes (a different range is a
  // different query). Switching data types reads from this map first so
  // a previously-loaded type comes back instantly; misses trigger a
  // silent background fetch.
  const [dataByType, setDataByType] = useState({});
  const [rawPhiCache, setRawPhiCache] = useState({ key: null, records: null });
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [facilityHierarchy, setFacilityHierarchy] = useState([]);
  // Flat list from /api/cache/facilities (CachedFacilities). Used to map
  // VitalEngine numeric facility IDs in records → Salesforce facility names
  // for chart legends. The hierarchy nodes are keyed by SalesforceId and
  // don't carry the VE numeric ID, so they can't be used for that lookup.
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState(new Set());
  const [showFacilityFilter, setShowFacilityFilter] = useState(false);
  const [chartMode, setChartMode] = useState('combined');

  const [showRaw, setShowRaw] = useState(false);
  const [loadingRaw, setLoadingRaw] = useState(false);
  const [loadingRawMessage, setLoadingRawMessage] = useState('');
  const [exportingFormatted, setExportingFormatted] = useState(false);
  const rawPhiFetchGen = useRef(0);

  const data = dataByType[selectedType] || [];

  // Formatted vs Raw column layout for the table beneath the chart. The
  // "Raw" view dumps every data.* column (legacy DataTable); "Formatted"
  // renders a per-data-type per-row layout matching Quick Refer.
  const [tableView, setTableView] = useState('formatted');
  const [tableSearch, setTableSearch] = useState('');

  // Studies cache rows carry both `study_sent` and `study_received` per
  // facility-day. The chart sums whichever direction(s) the user picks here.
  // Default 'total' preserves the historical chart numbers.
  const [studyMetric, setStudyMetric] = useState('total');

  const [savedFilters, setSavedFilters] = useState([]);
  const [activeFilterId, setActiveFilterId] = useState('');

  const { customers, resolveCustomerFacilityIds } = useCustomerFilter();
  const [activeCustomerId, setActiveCustomerId] = useState('');

  // Compact filter strip vs full-form view. Starts expanded so first-
  // time visitors see the dropdowns; collapses to the slim summary the
  // moment data is loaded so the analysis itself owns the screen.
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [showScopeFacilities, setShowScopeFacilities] = useState(false);

  // Inbound / outbound / either direction filter for the facility scope.
  // Persisted across reloads. Only applies to data types that carry
  // both sender and receiver facility IDs (referrals / vitalrounds /
  // group-referrals).
  const [direction, setDirectionState] = useState(loadDirectionPreference);
  const setDirection = (v) => {
    setDirectionState(v);
    saveDirectionPreference(v);
  };
  const directionApplies = dataTypeHasDirection(selectedType);

  // Cross-page scope (drives navigation: scope set on this page sticks
  // across pages until cleared). The Clear button on the green Viewing
  // bar must wipe this too, otherwise navigating to another page would
  // re-load the cleared scope from sessionStorage.
  const { clearScope: clearGlobalScope } = useScope();

  // Single source of truth for "what is the user analyzing" so the
  // page title and the green scope bar stay in sync regardless of
  // which scope source the user used (Customer dropdown, Saved Filter
  // dropdown, or Custom Filter modal).
  const currentScope = useCurrentScope({
    activeCustomerId,
    activeFilterId,
    selectedFacilityIds,
    customers,
    savedFilters,
    setActiveCustomerId,
    setActiveFilterId,
    setSelectedFacilityIds,
    clearGlobalScope,
  });

  // Human label for the date-range preset shown in the compact strip.
  const dateRangeLabel = useMemo(() => {
    const presetNames = {
      last_14_days: 'Last 14 Days',
      last_month: 'Last 30 Days',
      last_3_months: 'Last 3 Months',
      last_6_months: 'Last 6 Months',
      last_year: 'Last 12 Months',
      last_18_months: 'Last 18 Months',
      last_2_years: 'Last 2 Years',
      custom: 'Custom Range',
    };
    const base = presetNames[dateRangePreset] || 'Date Range';
    if (startDate && endDate) {
      return `${base} (${formatDateShort(startDate)} - ${formatDateShort(endDate)})`;
    }
    return base;
  }, [dateRangePreset, startDate, endDate]);

  const selectedTypeLabel = useMemo(() => {
    const t = DATA_TYPES.find(x => x.id === selectedType);
    return t?.name || selectedType;
  }, [selectedType]);

  useEffect(() => {
    loadFacilityHierarchy();
    loadFacilities();
    loadSavedFilters();
    const { start, end } = calculateDateRange('last_month');
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (dateRangePreset !== 'custom') {
      const { start, end } = calculateDateRange(dateRangePreset);
      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }
  }, [dateRangePreset]);

  // Date range is part of the cache key — invalidate everything when it
  // changes so the user doesn't see stale records under a new window.
  // Also forces them to click Load Data again, matching prior behavior.
  useEffect(() => {
    setDataByType({});
    setRawPhiCache({ key: null, records: null });
    setHasLoaded(false);
    setShowRaw(false);
  }, [startDate, endDate]);

  // Drop any in-flight or completed PHI when scope changes so a prior
  // all-facilities fetch cannot bleed into a narrowed facility filter.
  useEffect(() => {
    rawPhiFetchGen.current += 1;
    setRawPhiCache({ key: null, records: null });
  }, [selectedFacilityIds, direction, selectedType]);

  const loadFacilityHierarchy = async () => {
    try {
      const response = await cache.getCachedFacilityHierarchy();
      if (response.success) {
        setFacilityHierarchy(response.hierarchy || []);
      }
    } catch {
      // Facilities not cached yet
    }
  };

  const loadFacilities = async () => {
    try {
      const response = await cache.getCachedFacilities();
      if (response?.success) {
        setFacilities(response.facilities || []);
      }
    } catch {
      // Facilities not cached yet — chart legend will fall back to "Facility {id}"
    }
  };

  const loadSavedFilters = async () => {
    try {
      const response = await cache.getSavedFilters();
      if (response?.filters) {
        setSavedFilters(response.filters);
      }
    } catch {
      // Filters table may not exist yet
    }
  };

  const applySavedFilter = (filterId) => {
    setActiveFilterId(filterId);
    setActiveCustomerId('');
    if (!filterId) {
      setSelectedFacilityIds(new Set());
    } else {
      const filter = savedFilters.find(f => f.id === parseInt(filterId));
      if (filter?.facilityIds) setSelectedFacilityIds(new Set(filter.facilityIds));
    }
    setScopeFromFilter(filterId);
  };

  const applyCustomerFilter = async (customerId) => {
    setActiveCustomerId(customerId);
    setActiveFilterId('');
    if (!customerId) { setSelectedFacilityIds(new Set()); }
    else {
      const ids = await resolveCustomerFacilityIds(customerId);
      setSelectedFacilityIds(new Set(ids));
    }
    setScopeFromCustomer(customerId);
  };

  // Fetch one data type and stash it in the per-type cache. The optional
  // `silent` flag is true for the background auto-fetch on type-switch
  // (so we don't kick the user out of raw-PHI view); the manual "Load
  // Data" button leaves it false.
  const loadCachedData = useCallback(async (typeOverride, { silent = false } = {}) => {
    const type = typeOverride || selectedType;
    if (!type || !startDate || !endDate) {
      setError('Please select data type and date range');
      return;
    }
    setLoading(true);
    setError('');
    if (!silent) setShowRaw(false);

    try {
      const response = await cache.getCachedData(type, startDate, endDate);
      if (response.success) {
        setDataByType(prev => ({ ...prev, [type]: response.records || [] }));
        setHasLoaded(true);
        if (!silent) setFiltersCollapsed(true);
      } else {
        setError('Failed to load cached data');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch cached data. Have you refreshed this data type?');
    } finally {
      setLoading(false);
    }
  }, [selectedType, startDate, endDate, cache]);

  // After the first Load Data click, switching data types should "just
  // work" — silently fetch any type the user hasn't loaded yet for the
  // current date range, and serve cached entries instantly. No extra
  // Load Data click required.
  useEffect(() => {
    if (!hasLoaded) return;
    if (!selectedType || !startDate || !endDate) return;
    if (dataByType[selectedType] !== undefined) return; // cache hit
    loadCachedData(selectedType, { silent: true });
  }, [selectedType, hasLoaded, dataByType, startDate, endDate, loadCachedData]);

  // Sticky scope: see useScopeSync.js. Reads ?customerId/?filterId/?facilityId
  // from the URL on mount, promotes them into the global scope context, and
  // syncs back any in-page selector changes so the StickyScopeBar mirrors
  // what the user is analyzing.
  const { setScopeFromCustomer, setScopeFromFilter } = useScopeSync({
    customers,
    savedFilters,
    resolveCustomerFacilityIds,
    setSelectedFacilityIds,
    setActiveCustomerId,
    setActiveFilterId,
    loadData: loadCachedData,
    ready: Boolean(selectedType && startDate && endDate),
  });

  const calculateDateRange = (preset) => {
    const end = new Date();
    const start = new Date();
    switch (preset) {
      case 'last_14_days': start.setDate(start.getDate() - 14); break;
      case 'last_month': start.setDate(start.getDate() - 30); break;
      case 'last_3_months': start.setMonth(start.getMonth() - 3); break;
      case 'last_6_months': start.setMonth(start.getMonth() - 6); break;
      case 'last_year': start.setFullYear(start.getFullYear() - 1); break;
      case 'last_18_months': start.setMonth(start.getMonth() - 18); break;
      case 'last_2_years': start.setFullYear(start.getFullYear() - 2); break;
      default: return { start: new Date(startDate), end: new Date(endDate) };
    }
    return { start, end };
  };

  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const applyFacilityFilter = useCallback((records) => {
    if (!records?.length) return [];
    if (selectedFacilityIds.size === 0) return records;
    return records.filter(record => {
      const inner = record.data || record.Data;
      if (!inner) return false;
      return rowMatchesFacilityScope(inner, selectedType, selectedFacilityIds, direction);
    });
  }, [selectedFacilityIds, selectedType, direction]);

  // Cached rows only — used for formatted export without PHI even when the
  // table is toggled to live raw PHI view.
  const filteredCachedData = useMemo(
    () => applyFacilityFilter(data),
    [data, applyFacilityFilter],
  );

  const rawFetchKey = useMemo(() => {
    const ids = extractCacheRecordIds(filteredCachedData);
    const facIds = selectedFacilityIds.size > 0
      ? Array.from(selectedFacilityIds).sort().join(',')
      : '';
    return `${selectedType}|${startDate}|${endDate}|${direction}|${facIds}|${ids.join(',')}`;
  }, [selectedType, startDate, endDate, direction, selectedFacilityIds, filteredCachedData]);

  const rawData = rawPhiCache.key === rawFetchKey ? rawPhiCache.records : null;

  const handleRawToggle = () => {
    if (showRaw) {
      setShowRaw(false);
      return;
    }
    if (!selectedType || !startDate || !endDate) return;
    setShowRaw(true);
  };

  // Paginated live PHI fetch — scoped to the same filtered cache rows on screen.
  useEffect(() => {
    if (!showRaw) return;
    if (!hasLoaded || !selectedType || !startDate || !endDate) return;
    if (rawPhiCache.key === rawFetchKey) return;

    let cancelled = false;
    const fetchGen = ++rawPhiFetchGen.current;
    const recordIds = extractCacheRecordIds(filteredCachedData);
    setLoadingRawMessage(
      recordIds.length > 0
        ? `Fetching PHI for ${recordIds.length} records from VitalEngine…`
        : 'Fetching PHI from VitalEngine…',
    );
    setLoadingRaw(true);

    (async () => {
      try {
        const response = await cache.getRawPhiData(selectedType, startDate, endDate, {
          recordIds,
          facilityIds: selectedFacilityIds.size > 0 ? Array.from(selectedFacilityIds) : [],
          direction: directionApplies ? direction : 'inbound',
        });
        if (cancelled || fetchGen !== rawPhiFetchGen.current) return;
        if (response?.success) {
          setRawPhiCache({ key: rawFetchKey, records: response.records || [] });
        } else {
          setError('Failed to fetch raw PHI data');
          setShowRaw(false);
        }
      } catch {
        if (!cancelled && fetchGen === rawPhiFetchGen.current) {
          setError('Failed to fetch raw PHI data');
          setShowRaw(false);
        }
      } finally {
        if (!cancelled && fetchGen === rawPhiFetchGen.current) {
          setLoadingRaw(false);
          setLoadingRawMessage('');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [
    showRaw,
    rawFetchKey,
    rawPhiCache.key,
    hasLoaded,
    selectedType,
    startDate,
    endDate,
    filteredCachedData,
    cache,
    selectedFacilityIds,
    direction,
    directionApplies,
  ]);

  const displayData = showRaw && rawData ? rawData : data;

  // Different data types expose the facility under different keys:
  //   referrals / vitalrounds  → receiving_facility_id  (the destination)
  //   studies / messages /
  //     groups / logins        → facility_id            (the only facility)
  // Fall back so the customer / saved-filter / facility-filter modal works
  // for *every* data type, not just referrals.
  const recordFacilityId = (recordData) => {
    const raw = recordData?.receiving_facility_id ?? recordData?.facility_id;
    if (raw == null || raw === '') return null;
    return Math.round(parseFloat(raw.toString())).toString();
  };

  const dataFacilityIds = useMemo(() => {
    const ids = new Set();
    displayData.forEach(record => {
      const fid = recordFacilityId(record.data || record.Data);
      if (fid) ids.add(fid);
    });
    return ids;
  }, [displayData]);

  const availableFacilityIds = dataFacilityIds;

  const filteredData = useMemo(() => {
    const source = showRaw && rawData ? rawData : data;
    return applyFacilityFilter(source);
  }, [showRaw, rawData, data, applyFacilityFilter]);

  useEffect(() => {
    setTableSearch('');
  }, [selectedType, startDate, endDate, showRaw, selectedFacilityIds, direction]);

  // Picks a sensible chart bin width from the loaded date span so the line
  // chart doesn't degrade into a flat strip of months for short ranges or a
  // jittery per-day comb for multi-year ones. The user can still override
  // via the chart's own "Group by" dropdown.
  //
  // The presets call setDate(getDate() - N), so end - start is exactly N
  // days for the "Last 14 Days" / "Last Month" buckets — round to the
  // nearest whole day so floating-point quirks don't push a 14-day window
  // into the 15-day "Week" bracket.
  const autoBinning = useMemo(() => {
    if (!startDate || !endDate) return 'month';
    const s = new Date(startDate);
    const e = new Date(endDate);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 'month';
    const days = Math.max(1, Math.round((e - s) / 86400000));
    if (days <= 14) return 'day';
    if (days <= 60) return 'week';
    return 'month';
  }, [startDate, endDate]);

  // Authoritative source: CachedFacilities (Salesforce-synced) keyed by the
  // VitalEngine numeric facility ID, which is the same ID the chart's
  // breakdown uses (sending_facility_id / receiving_facility_id /
  // facility_id from VitalEngine records). Without this lookup the chart
  // legend falls back to "Facility {id}" for every data type whose records
  // don't carry a name field (e.g. Faxes, Studies, Logins).
  //
  // Records *do* sometimes include the name (referrals carry
  // `receiving_facility`); we layer those on second so the map still works
  // before the facilities list has loaded, but the SF source wins when both
  // are available.
  const facilityNamesMap = useMemo(() => {
    const map = new Map();

    facilities.forEach(facility => {
      const veId = facility.vitalEngine_Facility_ID__c;
      if (!veId || !facility.name) return;
      const raw = veId.toString();
      map.set(raw, facility.name);
      const normalized = Math.round(parseFloat(raw)).toString();
      if (normalized !== raw) map.set(normalized, facility.name);
    });

    filteredData.forEach(record => {
      const recordData = record.data || record.Data;
      const fid = recordFacilityId(recordData);
      const name = recordData?.receiving_facility ?? recordData?.facility_name;
      if (fid && name && !map.has(fid)) map.set(fid, name);
    });

    return map;
  }, [facilities, filteredData]);

  const handleFormattedExport = async (format, includePhi) => {
    if (!isFormattedReferralExportType(selectedType)) return;
    if (!startDate || !endDate) return;

    const exportOpts = {
      dataType: selectedType,
      startDate,
      endDate,
      includePhi,
    };

    if (!includePhi) {
      if (format === 'csv') {
        exportFormattedReferralsToCsv(filteredCachedData, exportOpts);
      } else {
        exportFormattedReferralsToXlsx(filteredCachedData, exportOpts);
      }
      return;
    }

    setExportingFormatted(true);
    try {
      const recordIds = extractCacheRecordIds(filteredCachedData);
      const facilityIds = selectedFacilityIds.size > 0
        ? Array.from(selectedFacilityIds)
        : [];
      const res = await client.post(
        '/api/cache/formatted-referrals/export-phi',
        {
          type: selectedType,
          startDate,
          endDate,
          format,
          direction: directionApplies ? direction : 'inbound',
          facilityIds,
          recordIds,
        },
        { responseType: 'blob', timeout: 600000, validateStatus: s => s >= 200 && s < 300 },
      );
      const contentType = res.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        let msg = 'Failed to fetch raw PHI data for export';
        try { msg = JSON.parse(text)?.error || msg; } catch { /* keep default */ }
        setError(msg);
        return;
      }
      const ext = format === 'csv' ? 'csv' : 'xlsx';
      const mime = format === 'csv'
        ? 'text/csv;charset=utf-8;'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type: mime });
      triggerDownload(blob, `${buildFilenameStem(selectedType, startDate, endDate, 'formatted_phi')}.${ext}`);
    } catch (err) {
      let msg = err?.message || 'Failed to fetch raw PHI data for export';
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          msg = JSON.parse(text)?.error || msg;
        } catch { /* keep default */ }
      }
      setError(msg);
    } finally {
      setExportingFormatted(false);
    }
  };

  const showFormattedDownload = isFormattedReferralExportType(selectedType);

  return (
    <>
      <div className="page-header">
        <h1>
          Raw Data
          <span className="page-header-scope">
            <span className="page-header-sep">&mdash;</span>
            <span className="page-header-name">{currentScope.name}</span>
          </span>
        </h1>
      </div>

      {filtersCollapsed && data.length > 0 ? (
        <div className="filter-strip-bar">
          <div className="filter-strip-summary">
            <div className="filter-strip-item">
              <span className="filter-strip-label">Date range</span>
              <span className="filter-strip-value">{dateRangeLabel}</span>
            </div>
            <div className="filter-strip-item">
              <span className="filter-strip-label">Data type</span>
              <span className="filter-strip-value">{selectedTypeLabel}</span>
            </div>
            {directionApplies && currentScope.kind !== 'none' && (
              <div className="filter-strip-item">
                <span className="filter-strip-label">Direction</span>
                <span
                  className="filter-strip-value"
                  title={DIRECTION_OPTIONS.find(o => o.value === direction)?.description || ''}
                >
                  {DIRECTION_OPTIONS.find(o => o.value === direction)?.short || direction}
                </span>
              </div>
            )}
            {selectedType === 'studies' && (
              <div className="filter-strip-item">
                <span className="filter-strip-label">Studies metric</span>
                <span className="filter-strip-value">{studyMetric.charAt(0).toUpperCase() + studyMetric.slice(1)}</span>
              </div>
            )}
            {selectedFacilityIds.size > 1 && (
              <div className="filter-strip-item">
                <span className="filter-strip-label">Chart mode</span>
                <span className="filter-strip-value">{chartMode === 'combined' ? 'Combined' : 'Stacked'}</span>
              </div>
            )}
            <div className="filter-strip-item">
              <span className="filter-strip-label">Source</span>
              <span
                className="filter-strip-value"
                title={showRaw ? 'Live API (raw, ephemeral PHI)' : 'Local database (anonymized cache)'}
              >
                {showRaw ? 'LIVE (raw PHI)' : 'Local (anonymized)'}
              </span>
            </div>
          </div>
          <div className="filter-strip-actions">
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-ghost"
              onClick={() => setFiltersCollapsed(false)}
            >
              Edit filters
            </button>
            <button
              type="button"
              className="filter-strip-btn filter-strip-btn-primary"
              onClick={() => loadCachedData()}
              disabled={loading || !selectedType || !startDate || !endDate}
            >
              {loading ? 'Loading...' : 'Reload'}
            </button>
          </div>
        </div>
      ) : (
      <div className="controls-panel">
        <DataTypeSelector
          dataTypes={DATA_TYPES}
          selectedType={selectedType}
          onSelectType={setSelectedType}
          disabled={loading}
        />

        <div className="date-range-selector">
          <label htmlFor="dateRangePreset">Date Range:</label>
          <select
            id="dateRangePreset"
            value={dateRangePreset}
            onChange={(e) => setDateRangePreset(e.target.value)}
            disabled={loading}
            className="date-range-dropdown"
          >
            <option value="last_14_days">Last 14 Days</option>
            <option value="last_month">Last 30 Days</option>
            <option value="last_3_months">Last 3 Months</option>
            <option value="last_6_months">Last 6 Months</option>
            <option value="last_year">Last 12 Months</option>
            <option value="last_18_months">Last 18 Months</option>
            <option value="last_2_years">Last 2 Years</option>
            <option value="custom">Custom Range</option>
          </select>
          {startDate && endDate && (
            <span
              className="date-range-hint"
              title="The exact start and end dates this range resolves to. All presets are trailing windows ending today."
            >
              {formatDateShort(startDate)} &ndash; {formatDateShort(endDate)}
            </span>
          )}
        </div>

        {dateRangePreset === 'custom' && (
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            disabled={loading}
          />
        )}

        {savedFilters.length > 0 && (
          <div className="saved-filter-selector">
            <label htmlFor="savedFilter">Saved Filter:</label>
            <select
              id="savedFilter"
              value={activeFilterId}
              onChange={(e) => applySavedFilter(e.target.value)}
              className="date-range-dropdown"
            >
              <option value="">None (show all)</option>
              {savedFilters.map(f => (
                <option key={f.id} value={f.id}>
                  {f.name} ({f.facilityIds?.length || 0})
                </option>
              ))}
            </select>
          </div>
        )}

        {customers.length > 0 && (
          <div className="saved-filter-selector">
            <label htmlFor="customerFilter">Customer:</label>
            <select
              id="customerFilter"
              value={activeCustomerId}
              onChange={(e) => applyCustomerFilter(e.target.value)}
              className="date-range-dropdown"
            >
              <option value="">None (show all)</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>
                  {c._depth > 0 ? '— ' : ''}{c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <button
          className="filter-button"
          onClick={() => setShowFacilityFilter(true)}
        >
          Custom Filter
          {selectedFacilityIds.size > 0 && ` (${selectedFacilityIds.size})`}
        </button>

        <button
          className="load-button"
          onClick={loadCachedData}
          disabled={loading || !selectedType || !startDate || !endDate}
        >
          {loading ? 'Loading...' : 'Load Data'}
        </button>

        {data.length > 0 && selectedFacilityIds.size > 1 && (
          <div className="chart-mode-selector">
            <label htmlFor="chartMode">Chart Mode:</label>
            <select
              id="chartMode"
              value={chartMode}
              onChange={(e) => setChartMode(e.target.value)}
              className="chart-mode-dropdown"
            >
              <option value="combined">Combined</option>
              <option value="stacked">Stacked (Compare)</option>
            </select>
          </div>
        )}

        {directionApplies && (
          <div
            className="chart-mode-selector"
            title="Inbound = the customer received it. Outbound = the customer sent it. Any = both directions. Only applies when a facility scope is set."
          >
            <label>Direction:</label>
            <DirectionToggle value={direction} onChange={setDirection} />
          </div>
        )}

        {data.length > 0 && selectedType === 'studies' && (
          <div className="chart-mode-selector">
            <label>Studies:</label>
            <div className="table-view-segmented" role="group" aria-label="Studies metric">
              <button
                type="button"
                className={`table-view-btn ${studyMetric === 'sent' ? 'active' : ''}`}
                onClick={() => setStudyMetric('sent')}
                aria-pressed={studyMetric === 'sent'}
              >Sent</button>
              <button
                type="button"
                className={`table-view-btn ${studyMetric === 'received' ? 'active' : ''}`}
                onClick={() => setStudyMetric('received')}
                aria-pressed={studyMetric === 'received'}
              >Received</button>
              <button
                type="button"
                className={`table-view-btn ${studyMetric === 'total' ? 'active' : ''}`}
                onClick={() => setStudyMetric('total')}
                aria-pressed={studyMetric === 'total'}
              >Total</button>
            </div>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}
        {cache.error && <div className="error-message">{cache.error}</div>}

        {data.length > 0 && (
          <div className={`data-info ${showRaw ? 'data-info-raw' : ''}`}>
            <strong>{filteredData.length}</strong> of <strong>{displayData.length}</strong> records
            {currentScope.kind !== 'none' && directionApplies && (
              <span
                className="collab-filter-direction"
                title={DIRECTION_OPTIONS.find(o => o.value === direction)?.description || ''}
              >
                {' '}({directionShortLabel(direction)})
              </span>
            )}
            {showRaw
              ? <span style={{ fontWeight: 600 }}> | Source: LIVE API (RAW PHI - ephemeral)</span>
              : <span> | Source: Local Database (Anonymized)</span>
            }
          </div>
        )}
      </div>
      )}

      {filtersCollapsed && (error || cache.error) && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error || cache.error}
        </div>
      )}

      {filtersCollapsed && data.length > 0 && (
        <div className={`data-info ${showRaw ? 'data-info-raw' : ''}`} style={{ marginBottom: '1rem' }}>
          <strong>{filteredData.length}</strong> of <strong>{displayData.length}</strong> records
          {currentScope.kind !== 'none' && directionApplies && (
            <span
              className="collab-filter-direction"
              title={DIRECTION_OPTIONS.find(o => o.value === direction)?.description || ''}
            >
              {' '}({directionShortLabel(direction)})
            </span>
          )}
        </div>
      )}

      <ScopeBar
        scope={currentScope}
        onViewFacilities={
          currentScope.kind !== 'none' && facilityHierarchy?.length && selectedFacilityIds.size
            ? () => setShowScopeFacilities(true)
            : null
        }
      />

      {showScopeFacilities && currentScope.kind !== 'none' && (
        <GroupFacilitiesModal
          groupName={currentScope.name}
          hierarchy={facilityHierarchy}
          facilityIds={Array.from(selectedFacilityIds)}
          onClose={() => setShowScopeFacilities(false)}
        />
      )}

      {data.length > 0 && (
        <div style={{ padding: '0 0 1rem' }}>
          <RawDataBanner
            showRaw={showRaw}
            onToggle={handleRawToggle}
            loading={loadingRaw}
            loadingMessage={loadingRawMessage}
          />
        </div>
      )}

      <div className="chart-panel">
        {loading || loadingRaw ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{loadingRaw ? (loadingRawMessage || 'Fetching raw PHI data from VitalEngine…') : 'Loading data...'}</p>
          </div>
        ) : filteredData.length > 0 ? (
          <DataChart
            data={filteredData}
            dataType={selectedType}
            chartMode={chartMode}
            selectedFacilityIds={selectedFacilityIds}
            facilityNamesMap={facilityNamesMap}
            defaultBinning={autoBinning}
            windowStart={startDate}
            windowEnd={endDate}
            studyMetric={studyMetric}
          />
        ) : data.length > 0 ? (
          <div className="empty-state">
            <p>No data matches the selected facility filter.</p>
          </div>
        ) : (
          <div className="empty-state">
            <p>No data cached yet for this date range.</p>
            <p>
              An administrator can populate the local cache from <strong>Admin → Data Sources</strong>,
              or use the <strong>Refresh</strong> button in the banner above.
            </p>
          </div>
        )}
      </div>

      {filteredData.length > 0 && !loading && (
        <>
          <div className="table-view-toggle">
            <span className="table-view-toggle-label">Table view:</span>
            <div className="table-view-segmented" role="group" aria-label="Table view">
              <button
                type="button"
                className={`table-view-btn ${tableView === 'formatted' ? 'active' : ''}`}
                onClick={() => setTableView('formatted')}
                aria-pressed={tableView === 'formatted'}
              >Formatted</button>
              <button
                type="button"
                className={`table-view-btn ${tableView === 'raw' ? 'active' : ''}`}
                onClick={() => setTableView('raw')}
                aria-pressed={tableView === 'raw'}
              >Raw</button>
            </div>
            <span className="table-view-download-label">Download raw:</span>
            <div className="table-view-download" role="group" aria-label="Download raw data">
              <button
                type="button"
                className="table-view-download-btn"
                onClick={() => exportRecordsToCsv(filteredData, { dataType: selectedType, startDate, endDate, deidentifyDob: !showRaw })}
                title="All filtered rows, every field (matches Raw table view). PHI only if Show Raw PHI is on."
              >CSV</button>
              <button
                type="button"
                className="table-view-download-btn"
                onClick={() => exportRecordsToXlsx(filteredData, { dataType: selectedType, startDate, endDate, deidentifyDob: !showRaw })}
                title="All filtered rows, every field (matches Raw table view). PHI only if Show Raw PHI is on."
              >Excel</button>
            </div>
            {showFormattedDownload && (
              <>
                <span className="table-view-download-label">Formatted:</span>
                <div className="table-view-download" role="group" aria-label="Download formatted referral data">
                  <button
                    type="button"
                    className="table-view-download-btn"
                    disabled={exportingFormatted}
                    onClick={() => handleFormattedExport('csv', false)}
                    title="Formatted referral columns from the local cache (masked PHI per Admin PHI settings)."
                  >CSV</button>
                  <button
                    type="button"
                    className="table-view-download-btn"
                    disabled={exportingFormatted}
                    onClick={() => handleFormattedExport('xlsx', false)}
                    title="Formatted referral columns from the local cache (masked PHI per Admin PHI settings)."
                  >Excel</button>
                  <button
                    type="button"
                    className={`table-view-download-btn table-view-download-btn-phi${exportingFormatted ? ' is-busy' : ''}`}
                    disabled={exportingFormatted}
                    onClick={() => handleFormattedExport('csv', true)}
                    title="Formatted columns with live PHI from VitalEngine (all matching rows; may take a minute for large date ranges)."
                  >
                    {exportingFormatted ? (
                      <><span className="btn-spinner" aria-hidden="true" /> Exporting…</>
                    ) : (
                      'CSV + PHI'
                    )}
                  </button>
                  <button
                    type="button"
                    className={`table-view-download-btn table-view-download-btn-phi${exportingFormatted ? ' is-busy' : ''}`}
                    disabled={exportingFormatted}
                    onClick={() => handleFormattedExport('xlsx', true)}
                    title="Formatted columns with live PHI from VitalEngine (all matching rows; may take a minute for large date ranges)."
                  >
                    {exportingFormatted ? (
                      <><span className="btn-spinner" aria-hidden="true" /> Exporting…</>
                    ) : (
                      'Excel + PHI'
                    )}
                  </button>
                </div>
                {exportingFormatted && (
                  <span className="table-view-export-status" role="status">
                    Building spreadsheet from VitalEngine — this may take a minute for large date ranges.
                  </span>
                )}
              </>
            )}
          </div>
          {tableView === 'formatted'
            ? (
              <FormattedDataTable
                data={filteredData}
                dataType={selectedType}
                searchTerm={tableSearch}
                onSearchChange={setTableSearch}
              />
            )
            : (
              <DataTable
                data={filteredData}
                dataType={selectedType}
                searchTerm={tableSearch}
                onSearchChange={setTableSearch}
              />
            )}
        </>
      )}

      {showFacilityFilter && (
        <FacilityFilter
          hierarchy={facilityHierarchy}
          availableFacilityIds={availableFacilityIds}
          selectedFacilityIds={selectedFacilityIds}
          onSelectionChange={(ids) => { setSelectedFacilityIds(ids); setActiveFilterId(''); setActiveCustomerId(''); }}
          onClose={() => setShowFacilityFilter(false)}
        />
      )}

    </>
  );
}

export default Analytics;
